import { useBlockPanelPropsContext } from "./panel-container";
import PanelContent from "./panel-content";
import { FloatingPanel, FloatingPanelHeader, FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import { CheckIcon, Languages, XIcon } from "lucide-react";
import { CheckCircledIcon, ValueNoneIcon } from "@radix-ui/react-icons";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { isBlockTranslated } from "../../blocks";
import { useMemo } from "react";
import { AVAILABLE_LOCALES } from "@babackapp/ui/components/translatable-field";


type TranslationMode =
  "not-translatable"
  | "auto"
  | "translatable";

export default function PanelTranslationState({
  mode,
  portal,
}: {
  mode: TranslationMode,
  portal?: boolean
}) {
  const blockProps = useBlockPanelPropsContext();

  if (!portal || blockProps.isWrapped) {
    return (
      <div className="bg-background">
        <FloatingPanelSeparator />
        <PanelTranslationStateContent mode={mode} />
      </div>
    );
  }

  if (!blockProps.containerRef?.current) return null;

  return createPortal(
    <FloatingPanel className="mt-2 z-10 w-full">
      <PanelTranslationStateContent mode={mode} />
    </FloatingPanel>
    , blockProps.containerRef.current);

}


function PanelTranslationStateContent({
  mode,
}: {
  mode: TranslationMode,
}) {
  const blockProps = useBlockPanelPropsContext();
  const isTranslated = isBlockTranslated(blockProps.locale, blockProps.block);
  const availableLocales = blockProps.localeCodes ?? [];
  const { t } = useTranslation("editor-blocks-library");
  const localesState = useMemo(() => (
    availableLocales.map((locale) => ({
        locale,
        isTranslated: isBlockTranslated(locale, blockProps.block),
        label:
          AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.label ||
          locale,
        flag: AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.flag,
      }),
    )
  ), [availableLocales, blockProps.block, blockProps.locale, isTranslated, t, blockProps.block?.content]);

  return (
    <div className="w-full">
        <FloatingPanelHeader>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <Languages className="h-4 w-4 mr-2 text-muted-foreground" />
              {t("editorBlocksLibrary.translationState.title", "Translations")}
            </div>
          </div>
        </FloatingPanelHeader>
        <FloatingPanelSeparator />
        <PanelContent>
          <div>
            {mode === "not-translatable" &&
							<div className="text-sm text-muted-foreground flex items-center gap-2">
								<CheckCircledIcon className="size-5 text-green-300" />
                {t("editorBlocksLibrary.translationState.notTranslatable", "This block cannot be translated.")}
							</div>
            }
            {mode === "auto" &&
							<div className="text-sm text-muted-foreground flex items-center gap-2">
								<CheckCircledIcon className="size-5 text-green-300" />
                {t("editorBlocksLibrary.translationState.translationAuto",
                  "This block will be automatically translated.")}
							</div>
            }
            {mode === "translatable" && isTranslated &&
							<div className="text-sm text-muted-foreground flex items-center gap-2">
								<CheckCircledIcon className="size-5 text-green-300" />
                {t("editorBlocksLibrary.translationState.translated", "This block has been translated.")}
							</div>
            }
            {mode === "translatable" && !isTranslated &&
							<div className="text-sm text-muted-foreground flex items-center gap-2">
								<ValueNoneIcon className="size-5 text-orange-300" />
                {t("editorBlocksLibrary.translationState.missingTranslation",
                  "This block needs to be translated in the current language.")}
							</div>
            }
            {mode === "translatable" &&
							<div className="flex items-center justify-start w-full gap-1 flex-wrap">
                {localesState.map(locale => (
                  <div key={locale.locale} className="relative flex items-center justify-center w-8 h-7">
                    <div className="text-lg">
                      {locale.flag}
                    </div>
                    <div className="absolute bottom-0 right-0">
                      {locale.isTranslated
                        ? <CheckIcon size={14} className="text-green-300" /> :
                        <XIcon size={14} className="text-destructive" />
                      }
                    </div>
                  </div>
                ))
                }
							</div>
            }
          </div>
        </PanelContent>
    </div>
  );
}
